import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { extractApiErrorMessage } from '../../../../utils/apiError'
import { createCurrency, getCurrencyDetail, updateCurrency } from '../api/currencyApi'

const ROUNDING_OPTIONS = [
  { value: 'ROUND_OFF', label: 'Round Off' },
  { value: 'ROUND_UP', label: 'Round Up' },
  { value: 'ROUND_DOWN', label: 'Round Down' },
]

const EMPTY_FORM = {
  currencyCode: '',
  currencyName: '',
  symbol: '',
  decimalPlaces: 2,
  roundingMethod: 'ROUND_OFF',
  exchangeRate: '',
}

export function useCurrencyEntryScreen() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const editingCode = searchParams.get('code') // e.g. ?code=USD

  const isEditing = Boolean(editingCode)
  const savingRef = useRef(false)

  const [form, setForm] = useState(EMPTY_FORM)
  const [isBase, setIsBase] = useState(false)
  const [isLoading, setIsLoading] = useState(isEditing)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  // Load existing currency details when editing
  useEffect(() => {
    if (!editingCode) return
    let active = true
    setIsLoading(true)
    setError('')

    getCurrencyDetail(editingCode)
      .then((detail) => {
        if (!active) return
        const currencyIsBase = detail?.isBase === 1 || detail?.is_base === 1
        setIsBase(currencyIsBase)
        setForm({
          currencyCode: detail?.currencyCode || detail?.currency_code || '',
          currencyName: detail?.currencyName || detail?.currency_name || '',
          symbol: detail?.symbol || '',
          decimalPlaces: detail?.decimalPlaces ?? detail?.decimal_places ?? 2,
          roundingMethod: detail?.roundingMethod || detail?.rounding_method || 'ROUND_OFF',
          exchangeRate: currencyIsBase
            ? '1.000000'
            : String(detail?.exchangeRate ?? detail?.exchange_rate ?? ''),
        })
      })
      .catch((err) => {
        if (!active) return
        setError(extractApiErrorMessage(err, 'Failed to load currency details.'))
      })
      .finally(() => {
        if (active) setIsLoading(false)
      })

    return () => {
      active = false
    }
  }, [editingCode])

  function patchForm(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }))
  }

  const completionPercent = useMemo(() => {
    const required = ['currencyCode', 'currencyName', 'symbol', 'exchangeRate']
    const optional = ['decimalPlaces', 'roundingMethod']
    const filledRequired = required.filter((k) => String(form[k] ?? '').trim().length > 0).length
    const filledOptional = optional.filter((k) => String(form[k] ?? '').trim().length > 0).length
    const total = required.length * 2 + optional.length
    const filled = filledRequired * 2 + filledOptional
    return Math.round((filled / total) * 100)
  }, [form])

  async function handleSave() {
    if (savingRef.current) return
    setError('')
    setSuccess('')

    // Validate required fields
    if (!form.currencyCode.trim()) { setError('Currency Code is required.'); return }
    if (!form.currencyName.trim()) { setError('Currency Name (Formal Name) is required.'); return }
    if (!form.symbol.trim()) { setError('Symbol is required.'); return }
    if (!isBase) {
      if (!form.exchangeRate || isNaN(Number(form.exchangeRate)) || Number(form.exchangeRate) <= 0) {
        setError('Exchange Rate must be a positive number.')
        return
      }
    }

    savingRef.current = true
    setIsSaving(true)

    const payload = {
      currencyCode: form.currencyCode.trim().toUpperCase(),
      currencyName: form.currencyName.trim(),
      symbol: form.symbol.trim(),
      decimalPlaces: Number(form.decimalPlaces) || 2,
      roundingMethod: form.roundingMethod,
      exchangeRate: isBase ? 1.0 : Number(form.exchangeRate),
    }

    try {
      if (isEditing) {
        await updateCurrency(editingCode, payload)
        setSuccess('Currency updated successfully!')
      } else {
        await createCurrency(payload)
        setSuccess('Currency registered successfully!')
      }
      setTimeout(() => navigate('/masters/currency/list'), 1200)
    } catch (err) {
      setError(extractApiErrorMessage(err, isEditing ? 'Failed to update currency.' : 'Failed to register currency.'))
    } finally {
      savingRef.current = false
      setIsSaving(false)
    }
  }

  return {
    state: {
      form,
      isEditing,
      isBase,
      isSaving,
      isLoading,
      error,
      success,
      completionPercent,
      roundingOptions: ROUNDING_OPTIONS,
    },
    actions: {
      patchForm,
      handleSave,
    },
  }
}
